import React, { useEffect, useState } from 'react'
import CallTakerServices from "@/CADServices/APIs/callTaker";
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTruck } from '@fortawesome/free-solid-svg-icons';

/** API returns UnitKeyValuePairs as a JSON string or occasionally as an array */
export function parseUnitKeyValuePairs(raw) {
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

/** One plain object per apparatus (MutualAidApparatusID differs per unit). */
export function buildMutualAidObjectsForRow(row) {
    const pairs = parseUnitKeyValuePairs(row.UnitKeyValuePairs);
    const agencyCode = row.AgencyCode ?? '';
    const agencyName = row.AgencyName ?? '';

    return pairs.map((pair) => ({
        ZoneAgreementID: null,
        MutualAidAgencyID: pair.MutualAidAgencyID,
        AgencyCode: agencyCode,
        AgencyName: agencyName,
        AgreementType: null,
        ResponseRank: null,
        Status: null,
        ResponseStatusID: null,
        NotifiedAt: null,
        Notes: null,
        UnitID: null,
        MutualAidApparatusID: pair.MutualAidApparatusID,
    }));
}

/** Parse `{"a":1}{"b":2}` or `{"a":1},{"b":2}` into objects (ignores whitespace between). */
export function parseConcatenatedJsonObjects(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return [];
    const s = raw.trim();
    try {
        const one = JSON.parse(s);
        return Array.isArray(one) ? one : [one];
    } catch {
        /* fall through */
    }
    const objects = [];
    let depth = 0;
    let start = -1;
    let inString = false;
    let escape = false;
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (escape) {
            escape = false;
            continue;
        }
        if (c === '\\' && inString) {
            escape = true;
            continue;
        }
        if (c === '"' && !escape) {
            inString = !inString;
            continue;
        }
        if (inString) continue;
        if (c === '{') {
            if (depth === 0) start = i;
            depth++;
        } else if (c === '}') {
            depth--;
            if (depth === 0 && start >= 0) {
                try {
                    objects.push(JSON.parse(s.slice(start, i + 1)));
                } catch {
                    /* skip malformed segment */
                }
                start = -1;
            }
        }
    }
    return objects;
}

export function parseStoredMutualAidItems(mutualAidResource) {
    if (!Array.isArray(mutualAidResource)) return [];
    return mutualAidResource.flatMap((item) => {
        if (item == null) return [];
        if (typeof item === 'string') {
            return parseConcatenatedJsonObjects(item);
        }
        if (typeof item !== 'object') {
            return [];
        }
        if (Object.prototype.hasOwnProperty.call(item, 'MutualAidJson')) {
            const inner = item.MutualAidJson;
            if (typeof inner === 'string') {
                return parseConcatenatedJsonObjects(inner);
            }
            if (Array.isArray(inner)) {
                return inner.filter(Boolean);
            }
        }
        if (item.MutualAidAgencyID != null || item.MutualAidApparatusID != null) {
            return [item];
        }
        return [];
    });
}

/** True when stored state includes each pair's agency + apparatus for this row. */
export function isRowRequested(row, mutualAidResource) {
    const pairs = parseUnitKeyValuePairs(row.UnitKeyValuePairs);
    if (!pairs.length) return false;
    const stored = parseStoredMutualAidItems(mutualAidResource);
    return pairs.every((pair) =>
        stored.some(
            (s) =>
                s.MutualAidAgencyID === pair.MutualAidAgencyID &&
                s.MutualAidApparatusID === pair.MutualAidApparatusID
        )
    );
}

export const styles = {
    wrap: {
        display: 'flex',
        border: '1px solid #d4c4a8',
        borderRadius: '2px',
        overflow: 'hidden',
        background: '#faf7ef',
        minWidth: 0,
    },
    accentBar: {
        width: '6px',
        flexShrink: 0,
        background: '#e07020',
    },
    inner: {
        flex: 1,
        minWidth: 0,
        padding: '8px',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        marginBottom: '12px',
    },
    truckIcon: {
        color: '#c62828',
        fontSize: '18px',
        marginTop: '2px',
    },
    titleBlock: {
        lineHeight: 1.25,
        display: 'flex',
        gap: '4px',
    },
    title: {
        fontWeight: 700,
        fontSize: '17px',
        color: '#7a3d12',
        margin: 0,
    },
    subtitle: {
        margin: '4px 0 0',
        fontSize: '12px',
        color: '#8b6914',
        fontWeight: 400,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px',
    },
    thRow: {
        background: '#f2ead9',
    },
    th: {
        textAlign: 'left',
        padding: '8px 10px',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: '#6b3a0f',
        textTransform: 'uppercase',
        borderBottom: '1px solid #e0d4bc',
    },
    thAction: {
        textAlign: 'right',
    },
    td: {
        padding: '4px',
        verticalAlign: 'middle',
        color: '#2c3e50',
        borderBottom: '1px solid #e8e0d0',
    },
    tdAgency: {
        fontWeight: 500,
        color: '#34495e',
    },
    tdEta: {
        color: '#7a8fa3',
        whiteSpace: 'nowrap',
    },
    tdAction: {
        textAlign: 'right',
    },
    badgeWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
    },
    badge: {
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '4px',
        background: '#dde6ef',
        color: '#1a3a5c',
        fontSize: '11px',
        fontWeight: 600,
    },
    rowTintA: {
        background: '#e8f4ea',
    },
    rowTintB: {
        background: '#e8f4ea',
    },
    rowTintC: {
        background: '#ffffff',
    },
    btnRequested: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid #5cb85c',
        background: '#e8f5e9',
        color: '#2e7d32',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'default',
        whiteSpace: 'nowrap',
    },
    btnRequest: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        border: 'none',
        background: '#e07020',
        color: '#fff',
        fontSize: '12px',
        fontWeight: 700,
        cursor: 'pointer',
    },
};

function MutualAid({ loginAgencyID, runCardData = [], mutualAidResource = [], setMutualAidResource = () => { } }) {
    const rowBg = [styles.rowTintA, styles.rowTintB, styles.rowTintC];
    const unitTypeIDs = [...new Set(
        (runCardData ?? [])
            .filter((item) => String(item?.Status ?? '').toUpperCase() !== 'AV')
            .map((item) => item?.ResourceTypeID)
            .filter((id) => id != null && id !== '')
    )].join(',');
    const [mutualAidResourceData, setMutualAidResourceData] = useState(null);

    const [requestedAtByRowKey, setRequestedAtByRowKey] = useState({});

    const handleRequestMutualAid = (row) => {
        const objects = buildMutualAidObjectsForRow(row);
        if (!objects.length) return;
        setMutualAidResource((prev) => [...(Array.isArray(prev) ? prev : []), ...objects]);
        const pairs = parseUnitKeyValuePairs(row.UnitKeyValuePairs);
        const rowKey = `${row.AgencyName}-${pairs[0]?.MutualAidAgencyID ?? 'na'}`;
        const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        setRequestedAtByRowKey((prev) => ({ ...prev, [rowKey]: timeStr }));
    };

    const getMutualAidResourceKey = `/CAD/MasterResource/GetMutualAidResource/${loginAgencyID}`;
    const { data: mutualAidResourceDataQuery, isSuccess: isFetchMutualAidResourceData } =
        useQuery(
            [getMutualAidResourceKey, { AgencyID: loginAgencyID, UnitTypeIDs: unitTypeIDs }],
            CallTakerServices.getMutualAidResource,
            {
                refetchOnWindowFocus: false,
                enabled: !!loginAgencyID && !!unitTypeIDs
            }
        );

    useEffect(() => {
        if (isFetchMutualAidResourceData && mutualAidResourceDataQuery) {
            const data = JSON.parse(mutualAidResourceDataQuery.data.data);
            setMutualAidResourceData(data.Table);
        } else {
            setMutualAidResourceData([]);
        }
    }, [isFetchMutualAidResourceData, mutualAidResourceDataQuery]);

    return (
        <>
            {mutualAidResourceData?.length > 0 && (
                <div className='mt-2' style={styles.wrap}>
                    <div style={styles.accentBar} aria-hidden />
                    <div style={styles.inner}>
                        <div style={styles.headerRow}>
                            <FontAwesomeIcon icon={faTruck} style={styles.truckIcon} />
                            <div style={styles.titleBlock}>
                                <h2 style={styles.title}>Mutual Aid</h2>
                                <p style={styles.subtitle}>— available for this address</p>
                            </div>
                        </div>

                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.thRow}>
                                    <th style={styles.th}>Agency</th>
                                    <th style={styles.th}>Apparatus available</th>
                                    <th style={styles.th}>Unit Type</th>
                                    <th style={{ ...styles.th, ...styles.thAction }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mutualAidResourceData?.length > 0 && mutualAidResourceData?.map((row, index) => {
                                    const pairs = parseUnitKeyValuePairs(row.UnitKeyValuePairs);
                                    const rowKey = `${row.AgencyName}-${pairs[0]?.MutualAidAgencyID ?? 'na'}`;
                                    const requested =
                                        row.status === 'requested' || isRowRequested(row, mutualAidResource);
                                    const ts = requestedAtByRowKey[rowKey] ?? row.timestamp;

                                    return (
                                        <tr key={`${row.AgencyName}-${index}`} style={rowBg[index % rowBg.length]}>
                                            <td style={{ ...styles.td, ...styles.tdAgency }}>{row.AgencyName}</td>
                                            <td style={styles.td}>
                                                <div style={styles.badgeWrap}>
                                                    {pairs.map((i) => (
                                                        <span key={i?.id ?? i?.name} style={styles.badge}>
                                                            {i?.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td style={{ ...styles.td, ...styles.tdEta }}>{row.ResourceTypeDescription}</td>
                                            <td style={{ ...styles.td, ...styles.tdAction }}>
                                                {requested ? (
                                                    <span style={styles.btnRequested} role="status">
                                                        <FontAwesomeIcon icon={faCheck} style={{ fontSize: '11px' }} />
                                                        Requested{ts != null && ts !== '' ? ` ${ts}` : ''}
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        style={styles.btnRequest}
                                                        onClick={() => handleRequestMutualAid(row)}
                                                    >
                                                        Request
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>)}
        </>
    );
}

/** Flat list of mutual-aid objects; parses string / MutualAidJson legacy rows. */
export function normalizeMutualAidResourceList(arr) {
    return parseStoredMutualAidItems(arr);
}

export default MutualAid;
